import { c as _c } from "react/compiler-runtime";
import { useQuery } from "react-query";
import type { JobLogsResponse } from "@/hooks/useJobDetails";
import { makeAbortError } from "@/lib/utils";
interface BlobChunk {
  chunk: number;
  url: string;
  lines: number;
  size_bytes: number;
}

/** Single log source entry (e.g. stdout/github-action) from logfiles[type][name] */
export interface JobLogsMetadataEntry {
  job_id: string;
  total_lines: number;
  total_steps: number;
  blobs: Record<string, BlobChunk[]>; // keyed by record_order
  created_at: string;
}

/** API response: job_id + logfiles[log_source_type][log_source_name] -> entry */
export interface JobLogsMetadata {
  job_id: string;
  logfiles: Record<string, Record<string, JobLogsMetadataEntry>>;
}

/** Legacy API shape (flat) – accepted at runtime for backward compatibility */
interface JobLogsMetadataLegacy {
  job_id?: string;
  total_lines: number;
  total_steps: number;
  blobs: Record<string, BlobChunk[]>;
  created_at: string;
}
function isNewLogsMetadataShape(m: unknown): m is JobLogsMetadata {
  return m != null && typeof m === "object" && "logfiles" in m && typeof (m as JobLogsMetadata).logfiles === "object";
}
function isLegacyLogsMetadataShape(m: unknown): m is JobLogsMetadataLegacy {
  if (m == null || typeof m !== "object" || !("blobs" in m)) {
    return false;
  }
  const o = m as Record<string, unknown>;
  return o.logfiles == null || typeof o.logfiles !== "object";
}

/**
 * Get the default log entry (stdout/github-action) or the first available entry.
 * Accepts both new (job_id + logfiles) and legacy (flat) API shapes for backward compatibility.
 */
export function getDefaultLogsMetadataEntry(metadata: JobLogsMetadata | JobLogsMetadataLegacy | null | undefined): JobLogsMetadataEntry | null {
  if (metadata == null) return null;
  if (isNewLogsMetadataShape(metadata)) {
    if (!metadata.logfiles) return null;
    const entry = metadata.logfiles["stdout"]?.["github-action"] ?? Object.values(metadata.logfiles).flatMap(byName => Object.values(byName))[0];
    return entry ?? null;
  }
  if (isLegacyLogsMetadataShape(metadata)) {
    return {
      job_id: metadata.job_id ?? "",
      total_lines: metadata.total_lines,
      total_steps: metadata.total_steps,
      blobs: metadata.blobs,
      created_at: metadata.created_at
    };
  }
  return null;
}

/** One log source in the selector: type, name, and the entry for blob loading */
export interface LogSourceOption {
  sourceType: string;
  sourceName: string;
  entry: JobLogsMetadataEntry;
}

/**
 * List all log source entries (for log-source selector).
 * New shape: flattens logfiles into an array. Legacy shape: returns a single default item.
 */
export function getAllLogSourceEntries(metadata: JobLogsMetadata | JobLogsMetadataLegacy | null | undefined): LogSourceOption[] {
  if (metadata == null) return [];
  if (isNewLogsMetadataShape(metadata)) {
    if (!metadata.logfiles) return [];
    const result: LogSourceOption[] = [];
    for (const [sourceType, byName] of Object.entries(metadata.logfiles)) {
      for (const [sourceName, entry] of Object.entries(byName)) {
        result.push({
          sourceType,
          sourceName,
          entry
        });
      }
    }
    return result;
  }
  if (isLegacyLogsMetadataShape(metadata)) {
    const entry = getDefaultLogsMetadataEntry(metadata);
    if (!entry) return [];
    return [{
      sourceType: "stdout",
      sourceName: "github-action",
      entry
    }];
  }
  return [];
}

/**
 * Get the log entry for a given source type/name, or null.
 */
export function getLogsMetadataEntryBySource(metadata: JobLogsMetadata | JobLogsMetadataLegacy | null | undefined, sourceType: string, sourceName: string): JobLogsMetadataEntry | null {
  if (metadata == null) return null;
  if (isNewLogsMetadataShape(metadata)) {
    return metadata.logfiles?.[sourceType]?.[sourceName] ?? null;
  }
  if (isLegacyLogsMetadataShape(metadata)) {
    // Legacy has no type/name; only the default entry exists
    if (sourceType === "stdout" && sourceName === "github-action") {
      return getDefaultLogsMetadataEntry(metadata);
    }
    return null;
  }
  return null;
}

/**
 * Human-readable label for a log source (for selector UI).
 * stdout + github-action → "Logs"; container + name → "Container: name"; else "type: name".
 */
export function getLogSourceDisplayLabel(sourceType: string, sourceName: string): string {
  if (sourceType === "stdout" && sourceName === "github-action") {
    return "Job logs";
  }
  if (sourceType === "container" || sourceType.toLowerCase() === "container") {
    return sourceName || "logs";
  }
  return `${sourceType}: ${sourceName}`;
}

/**
 * Get logs metadata from the job details query (which includes logs_metadata).
 *
 * Reads exclusively through a disabled useQuery observer on the job details
 * key owned by useJobDetails. Do not read the cache imperatively here
 * (queryClient.getQueryData/getQueryState during render): React Compiler
 * memoizes those calls on the stable [queryClient, key] inputs, so they run
 * once while the query is still idle and never observe the resolved data —
 * permanently hiding logs_metadata on cold page loads.
 */
export function useJobLogsMetadata(org, jobId, runId) {
  const $ = _c(9);
  const hasJobDetailsQuery = !!runId;
  const t0 = runId ?? "";
  let t1;
  if ($[0] !== jobId || $[1] !== org || $[2] !== t0) {
    t1 = ["jobDetails", org, t0, jobId];
    $[0] = jobId;
    $[1] = org;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      enabled: false,
      staleTime: Infinity
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const jobDetailsQuery = useQuery(t1, _temp, t2);
  const jobDetails = hasJobDetailsQuery ? jobDetailsQuery.data : null;
  const t3 = jobDetails?.job?.logs_metadata ?? null;
  const t4 = hasJobDetailsQuery && jobDetailsQuery.isLoading;
  const t5 = hasJobDetailsQuery ? jobDetailsQuery.error ?? null : null;
  let t6;
  if ($[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
    t6 = {
      data: t3,
      isLoading: t4,
      error: t5
    };
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}

/**
 * Download and decompress a single gzipped blob
 */
function _temp() {
  return null;
}
async function fetchBlobChunk(url: string, signal?: AbortSignal): Promise<any[]> {
  const response = await fetch(url, {
    priority: "low",
    signal
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch blob: ${response.statusText}`);
  }
  const json = await response.json();
  return json;
}

/**
 * Download all blobs for a single log source entry in parallel
 */
export async function downloadAllBlobs(entry: JobLogsMetadataEntry, onProgress?: (loaded: number, total: number) => void, signal?: AbortSignal): Promise<JobLogsResponse> {
  if (signal?.aborted) {
    throw makeAbortError();
  }
  const allChunks: Array<BlobChunk> = [];
  for (const chunks of Object.values(entry.blobs)) {
    for (const chunk of chunks) {
      allChunks.push(chunk);
    }
  }
  let loaded = 0;
  const total = allChunks.length;
  const downloadedChunks = await Promise.all(allChunks.map(async chunk => {
    const data = await fetchBlobChunk(chunk.url, signal);
    if (signal?.aborted) {
      throw makeAbortError();
    }
    loaded++;
    if (!signal?.aborted) {
      onProgress?.(loaded, total);
    }
    return {
      chunk: chunk.chunk,
      data
    };
  }));

  // Flatten all log lines from all chunks (raw shape may be missing fields like log_idx/level/record_id)
  const rawLines: any[] = downloadedChunks.flatMap(c => c.data);

  // Sort globally by log_timestamp (format: YYYY-MM-DD HH:MM:SS[.fraction])
  // Lexicographic compare is safe for this format
  rawLines.sort((a, b) => {
    const at = String(a.log_timestamp || "");
    const bt = String(b.log_timestamp || "");
    if (at === bt) return 0;
    return at < bt ? -1 : 1;
  });

  // Recompute per-step line numbers and global log_idx
  const perStepCounters = new Map<number, number>();
  const hydrated: JobLogsResponse = rawLines.map((line, index) => {
    const recordOrder: number = Number.isFinite(line?.record_order) ? Number(line.record_order) : 0;
    const currentCount = perStepCounters.get(recordOrder) || 0;
    const nextLineNumber = currentCount + 1;
    perStepCounters.set(recordOrder, nextLineNumber);
    return {
      log_timestamp: String(line?.log_timestamp || ""),
      log_idx: index + 1,
      // 1-based index across entire job
      log_line_number: nextLineNumber,
      // 1-based within step when sorted by time
      message: String(line?.message ?? ""),
      // Ensure record_name is present (string); default to empty string if missing
      record_name: String(typeof line?.record_name === "string" ? line.record_name : ""),
      record_id: String(typeof line?.record_id === "string" ? line.record_id : ""),
      record_order: recordOrder,
      level: typeof line?.level === "string" ? line.level : "info"
    };
  });
  return hydrated;
}